import { useApolloClient } from "@apollo/client";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  type CrmSearchHit,
  CustomerServiceCrmPhoneMatchesDocument,
  type CustomerServiceCrmPhoneMatchesQuery,
  type CustomerServiceCrmPhoneMatchesQueryVariables,
} from "../../generated/graphql";
import { crmRecordPath } from "../../lib/crm";
import { captureException } from "../../lib/errorHandling";
import { isSameCallRecord } from "../../lib/telephony/callRecord";
import type { ActiveCall, DialRecord } from "../../lib/telephony/phoneEngine";

export type CallerMatches = {
  state: "idle" | "loading" | "none" | "several" | "linked" | "error";
  hits: CrmSearchHit[];
  /** The record the call is linked to. */
  linked: CrmSearchHit | null;
};

export const NO_CALLER_MATCHES: CallerMatches = { state: "idle", hits: [], linked: null };

/**
 * Looks up the caller of an incoming call once this tab answered it and opens the matching CRM record.
 * The agent links the call to a record: automatically on one match, by choosing among several,
 * or through the record page that is open (the phone panel offers that for any running call).
 * A call that already has a record keeps it: a lookup that finishes later neither opens nor links anything.
 */
export const useCallerMatches = (
  activeCall: ActiveCall | null,
  linkRecord: (record: DialRecord) => void
): {
  callerMatches: CallerMatches;
  chooseCallerMatch: (hit: CrmSearchHit) => void;
  retryCallerLookup: () => void;
} => {
  const client = useApolloClient();
  const navigate = useNavigate();
  const [callerMatches, setCallerMatches] = useState<CallerMatches>(NO_CALLER_MATCHES);
  // Counts the agent's retries of a failed lookup; each one runs the lookup effect again.
  const [lookupAttempt, setLookupAttempt] = useState(0);
  // The router renews navigate on every location change, which must not start another lookup.
  const navigateRef = useRef(navigate);
  const linkRecordRef = useRef(linkRecord);
  // The record the call has when the lookup finishes, which may be later than when it started.
  const callRecordRef = useRef<DialRecord | null>(null);
  const activeRecord = activeCall?.record ?? null;
  useEffect(() => {
    navigateRef.current = navigate;
    linkRecordRef.current = linkRecord;
    callRecordRef.current = activeRecord;
  }, [navigate, linkRecord, activeRecord]);

  const answeredInbound = activeCall?.direction === "inbound" && activeCall.phase === "connected";
  const callerNumber = activeCall?.number ?? null;

  const openRecord = useCallback((hit: CrmSearchHit) => {
    navigateRef.current(crmRecordPath(hit.object, hit.salesforceId));
    linkRecordRef.current({ object: hit.object, salesforceId: hit.salesforceId });
  }, []);

  useEffect(() => {
    if (!answeredInbound) {
      setCallerMatches(NO_CALLER_MATCHES);
      return undefined;
    }
    if (callerNumber === null) {
      setCallerMatches({ state: "none", hits: [], linked: null });
      return undefined;
    }

    let cancelled = false;
    setCallerMatches({ state: "loading", hits: [], linked: null });
    const lookUp = async () => {
      let hits: CrmSearchHit[] = [];
      let failed = false;
      try {
        const result = await client.query<
          CustomerServiceCrmPhoneMatchesQuery,
          CustomerServiceCrmPhoneMatchesQueryVariables
        >({
          query: CustomerServiceCrmPhoneMatchesDocument,
          variables: { number: callerNumber },
          fetchPolicy: "network-only",
        });
        hits = result.data.customerServiceCrmPhoneMatches;
      } catch (error) {
        captureException(error, "telephony/phone: caller lookup");
        failed = true;
      }
      if (cancelled) return;
      if (failed) {
        setCallerMatches({ state: "error", hits: [], linked: null });
        return;
      }
      const callRecord = callRecordRef.current;
      let linked: CrmSearchHit | null = null;
      if (callRecord) {
        linked = hits.find(hit => isSameCallRecord(callRecord, hit)) ?? null;
      } else if (hits.length === 1) {
        openRecord(hits[0]);
        [linked] = hits;
      }
      if (linked) setCallerMatches({ state: "linked", hits, linked });
      else setCallerMatches({ state: hits.length === 0 ? "none" : "several", hits, linked: null });
    };
    void lookUp();
    return () => {
      cancelled = true;
    };
  }, [client, answeredInbound, callerNumber, openRecord, lookupAttempt]);

  const chooseCallerMatch = useCallback(
    (hit: CrmSearchHit) => {
      if (callerMatches.state !== "several") return;
      openRecord(hit);
      setCallerMatches({ ...callerMatches, state: "linked", linked: hit });
    },
    [callerMatches, openRecord]
  );

  const retryCallerLookup = useCallback(() => setLookupAttempt(attempt => attempt + 1), []);

  return { callerMatches, chooseCallerMatch, retryCallerLookup };
};
